// src/components/operationCenter/widgets/FedRateProbabilitiesWidget.tsx
// Compact Fed Rate Probabilities widget for Operation Center

import React, { useMemo, useState } from "react";
import { TrendingDown, Minus, TrendingUp, Percent } from "lucide-react";
import type { WidgetProps } from "@/types/dashboard";
import { useFedRateProbabilities } from "@/hooks/macro/useFedRateProbabilities";
import { transformFedRateProbabilities } from "@/services/queries/macro/fedRateProbabilitiesAdapter";

const FedRateProbabilitiesWidget: React.FC<WidgetProps> = () => {
  const [selectedMeetingIdx, setSelectedMeetingIdx] = useState(0);

  const { data: fedRateData, isLoading, error } = useFedRateProbabilities();

  const { currentRateRange, currentFedRateUpper, meetingDates } = useMemo(() => {
    if (!fedRateData) {
      return {
        currentRateRange: "—",
        currentFedRateUpper: null,
        meetingDates: [],
      };
    }
    return transformFedRateProbabilities(fedRateData);
  }, [fedRateData]);

  const currentMeetingData = meetingDates[selectedMeetingIdx];

  const formatMeetingDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (error || !fedRateData) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500 p-4">
        <Percent className="w-8 h-8 mb-2 opacity-50" />
        <div className="text-sm">
          {error ? (error as Error).message : "No data available"}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/50 bg-slate-800/30 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Percent className="w-3.5 h-3.5 text-blue-400" />
          <span className="text-xs uppercase tracking-wide text-slate-300">
            Fed Rate Probabilities
          </span>
        </div>
        <div className="text-[10px] text-yellow-400 font-medium">
          Current: {currentRateRange} bps
          {currentFedRateUpper && ` (${currentFedRateUpper.toFixed(2)}%)`}
        </div>
      </div>

      {/* Meeting Date Tabs */}
      {meetingDates.length > 0 && (
        <div className="px-2 py-1.5 border-b border-slate-700/30 flex-shrink-0 overflow-x-auto">
          <div className="flex gap-1">
            {meetingDates.slice(0, 6).map((meeting, idx) => (
              <button
                key={meeting.meetingDate}
                onClick={() => setSelectedMeetingIdx(idx)}
                className={`px-2 py-1 text-[10px] font-medium rounded transition-colors whitespace-nowrap ${
                  selectedMeetingIdx === idx
                    ? "bg-blue-500 text-white"
                    : "text-slate-400 hover:text-white hover:bg-slate-700/50"
                }`}
              >
                {formatMeetingDate(meeting.meetingDate)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Probability Summary */}
      {currentMeetingData && (
        <div className="flex-1 p-3 overflow-y-auto">
          {/* Three probability cards */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            {/* Rate Cuts */}
            <div className="bg-slate-800/40 rounded-lg p-2 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <TrendingDown className="w-3 h-3 text-emerald-400" />
                <span className="text-[10px] text-slate-400">Cuts</span>
              </div>
              <div className="text-lg font-bold text-emerald-400">
                {currentMeetingData.totalCutsProbability.toFixed(1)}%
              </div>
            </div>

            {/* No Change */}
            <div className="bg-slate-800/40 rounded-lg p-2 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Minus className="w-3 h-3 text-yellow-400" />
                <span className="text-[10px] text-slate-400">Hold</span>
              </div>
              <div className="text-lg font-bold text-yellow-400">
                {currentMeetingData.noChangeProbability.toFixed(1)}%
              </div>
            </div>

            {/* Rate Hikes */}
            <div className="bg-slate-800/40 rounded-lg p-2 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <TrendingUp className="w-3 h-3 text-red-400" />
                <span className="text-[10px] text-slate-400">Hikes</span>
              </div>
              <div className="text-lg font-bold text-red-400">
                {currentMeetingData.rateHikesProbability.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Probability Distribution Bars */}
          <div className="space-y-1.5">
            <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-2">
              Target Rate Distribution
            </div>
            {currentMeetingData.probabilities
              .filter((p) => p.probability > 0.5)
              .sort((a, b) => b.probability - a.probability)
              .slice(0, 5)
              .map((prob) => (
                <div key={prob.rateRange} className="flex items-center gap-2">
                  <div className="w-16 text-[10px] text-slate-400 truncate">
                    {prob.rateRange}
                  </div>
                  <div className="flex-1 h-2 bg-slate-700/50 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        prob.isCurrentRate
                          ? "bg-yellow-500"
                          : "bg-blue-500"
                      }`}
                      style={{ width: `${Math.min(prob.probability, 100)}%` }}
                    />
                  </div>
                  <div className="w-10 text-[10px] text-slate-300 text-right tabular-nums">
                    {prob.probability.toFixed(1)}%
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FedRateProbabilitiesWidget;

